import { useEffect, useState } from 'react';
import { useChatContext } from './ChatContext';
import { STREAM_STAGE_LABELS, STREAMING_STATUS_MESSAGES } from '@/i18n/maps';
import { ART } from '@/art';
import type { StreamStage } from '@/types/chat';

const STATUS_INTERVAL_MS = 2500;

/**
 * While an answer is on its way: his face, three dots, and what the backend is doing — its
 * stage, once the stream has said (analyse → recherche → rédaction); rotating lines until then.
 */
export function TypingIndicator({ stage }: { stage?: StreamStage }): JSX.Element {
  const { locale } = useChatContext();
  const messages = STREAMING_STATUS_MESSAGES[locale] ?? STREAMING_STATUS_MESSAGES.fr;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (stage) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % messages.length);
    }, STATUS_INTERVAL_MS);
    return () => clearInterval(id);
  }, [messages.length, stage]);

  const line = stage ? (STREAM_STAGE_LABELS[locale] ?? STREAM_STAGE_LABELS.fr)[stage] : messages[index];

  return (
    <section
      role="status"
      aria-live="polite"
      className="tw-rounded-2xl tw-rounded-tl-sm tw-bg-cal-green-50 tw-border tw-border-cal-green-100 tw-p-4 tw-text-sm tw-text-cal-green-700 tw-flex tw-items-center tw-gap-3"
    >
      <img src={ART.typingAvatar} alt="" className="tw-h-8 tw-w-auto tw-flex-shrink-0" />
      <Dots />
      <span key={line} style={{ animation: 'cal-fade-in 400ms ease-out' }}>
        {line}
      </span>
    </section>
  );
}

export function Dots(): JSX.Element {
  return (
    <span className="tw-inline-flex tw-gap-1 tw-flex-shrink-0" aria-hidden="true">
      <span
        className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-cal-green-700"
        style={{ animation: 'cal-pulse 1.4s ease-in-out infinite' }}
      />
      <span
        className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-cal-green-700"
        style={{ animation: 'cal-pulse 1.4s ease-in-out 0.2s infinite' }}
      />
      <span
        className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-cal-green-700"
        style={{ animation: 'cal-pulse 1.4s ease-in-out 0.4s infinite' }}
      />
    </span>
  );
}
